/*eslint max-lines-per-function: ["error", 380]*/
/*eslint complexity: ["error", 53]*/
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useNavigation } from '../../../common/navigation';
import {
  CreateUpdateExamDto,
  GetExamBlockDto,
  GetExamDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { ExamEntity } from '../../../common/spdCore/contexts/ExamContext';
import { ExamDeleteLink } from '../../components/Exam/ExamDeleteLink';
import { ExamEndLink } from '../../components/Exam/ExamEndLink';
import { ExamTests } from '../../components/ExamTests/ExamTests';
import {
  ArticleMode,
  SupportingType,
} from '../../../common/spdCore/validation/schemas';
import { ExamSpecialistRoleEndedLink } from '../../components/Exam/ExamSpecialistRoleEndedLink';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { ExamOnline } from '../../components/ExamsPackage/ExamOnline';
import { ExamTestEntity } from '../../../common/spdCore/contexts/ExamTestContext';
import { SupportingExaminationView } from '../SupportingExaminationView/SupportingExaminationView';
import { useAuth } from '../../../common/hooks/useAuth';
import { ALL_WORKERS_REGISTRATION } from '../../../common/spdCore/authorization/roles';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { CompanyPrepaymentAlert } from '../../components/Company/CompanyPrepaymentAlert';
import { ExamSynevoTab } from '../../components/Synevo/ExamSynevoTab';
import CopyButton from '../../../common/components/CopyButton';
import { ExamBlockModal } from '../../components/ExamJudgments/ExamBlockModal';

import { AlertExamBlock } from './AlertExamBlock';
import { AlertHarmfulFactorsAndCriticalPoints } from './AlertHarmfulFactorsAndCriticalPoints';
import { ExamExaminedInfo } from './ExamExaminedInfo';
import { AlertConsultation } from './AlertConsultation';
import { ExamPaymentEditor } from './ExamPaymentInfo';
import { ExamReceiptEditor } from './ExamReceiptEditor';
import { ExamInvoicesList } from './ExamInvoicesList';
import { ExamReceiptsList } from './ExamReceiptsList';
import { ExamPortalPaymentAlert } from './ExamPortalPaymentAlert';

type ParamTypes = {
  id: string;
  mode?: ArticleMode;
};

export const ExamTestsView: React.FC = () => {
  const spd = useSpdCore();
  const nav = useNavigation();
  const auth = useAuth();
  const [exam, setExam] = useState<ExamEntity>();
  const [examTests, setExamTests] = useState<ExamTestEntity[]>();
  const [show, setShow] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [activeBlock, setActiveBlock] = useState<GetExamBlockDto | null>(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showEditBlockModal, setShowEditBlockModal] = useState(false);

  const { id, mode: rawMode } = useParams<ParamTypes>() as {
    id: string;
    mode?: string;
  };
  const mode =
    rawMode === 'null' ? null : (rawMode as ArticleMode | null | undefined);

  useEffect(() => {
    spd.exams
      .getExam(id)
      .then(ex => setExam(ex))
      .catch(error =>
        console.log(`Wystąpił problem z odświeżeniem strony. ${error}`),
      );
  }, [id, spd.exams, refresh]);

  useEffect(() => {
    if (!id) {
      return;
    }
    spd.examBlocks
      .getActive(id)
      .then(setActiveBlock)
      .catch(() => setActiveBlock(null));
  }, [id, spd.examBlocks]);

  const handleBlock = async (reason: string) => {
    const block = await spd.examBlocks.block(id, reason);
    setActiveBlock(block);
    setShowBlockModal(false);
  };

  const handleUnblock = async () => {
    await spd.examBlocks.unblock(id);
    setActiveBlock(null);
  };

  const handleUpdateBlock = async (reason: string) => {
    const updated = await spd.examBlocks.update(id, reason);
    setActiveBlock(updated);
    setShowEditBlockModal(false);
  };

  useEffect(() => {
    if (id) {
      spd.examTests
        .getByExam(id)
        .then(ex => setExamTests(ex))
        .catch(error =>
          console.log(`Wystąpił problem z odświeżeniem strony. ${error}`),
        );
    }
  }, [id, spd.examTests]);

  const handleClick = async (id: string) => {
    void nav.indicatorTestsInExam(id);
  };

  const onExamChanged = async (
    exam: Pick<GetExamDto, 'id' | 'endDate' | 'startDate'>,
  ) => {
    return spd.exams
      .changeDates(exam.id, exam.endDate as Date, exam.startDate as Date)
      .then(() =>
        spd.exams.getExam(id).then(ex => {
          setExam(ex);
        }),
      )
      .catch(async error => {
        const err = await responseErrors(error);
        toast.error(`Wystąpił problem podczas zamykania badania. ${err}`);
      });
  };

  const getCount = useCallback(
    async (filterText?: string) => {
      return spd.examTests.getForExamCount(filterText, id);
    },
    [spd.examTests, id],
  );
  const handleClickExamined = () => {
    setShow(true);
  };
  const handleCloseExamined = () => {
    setShow(false);
    setRefresh(prev => !prev);
  };

  const handleOnRefresh = useCallback(() => {
    setRefresh(prev => !prev);
  }, []);
  const editPayment = async (examCU: CreateUpdateExamDto) => {
    try {
      if (exam?.id) {
        await spd.exams.update(exam.id, examCU);
        handleOnRefresh();
      }
      return {
        saved: true,
        errors: [],
      };
    } catch (response) {
      return {
        saved: false,
        errors: await responseErrors(response as ResponseError),
      };
    }
  };
  const getData = useCallback(
    async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) => {
      return spd.examTests.getForExamByPortion(
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        id,
      );
    },
    [spd.examTests, id],
  );

  return (
    <Card>
      <ExamExaminedInfo
        exam={exam}
        show={show}
        handleClickExamined={handleClickExamined}
        handleCloseExamined={handleCloseExamined}
        mode={mode ? mode.toString() : undefined}
      />
      {exam && !exam.active && (
        <Alert variant="danger" className="mx-3 mt-3 mb-0 fw-semibold">
          UWAGA! BADANIE ZOSTAŁO USUNIĘTE!
        </Alert>
      )}
      <TabsHistory activeKey="examTests" id="examTests" className="mb-3">
        <Tab
          eventKey="examTests"
          title={
            <>
              {`Testy - Badanie ${exam?.number || ''}`}
              {exam?.number && (
                <CopyButton
                  tooltipText="Kopiuj numer badania"
                  value={exam?.number?.toString()}
                />
              )}
              {` (Paczka: ${exam?.examPackage?.number || ''}) Opis: ${exam?.description || '-'}`}
            </>
          }
          className="m-3"
        >
          <ToastContainer />
          <AlertExamBlock
            activeBlock={activeBlock}
            onUnblock={handleUnblock}
            onEdit={() => setShowEditBlockModal(true)}
          />
          <AlertHarmfulFactorsAndCriticalPoints exam={exam} />
          <AlertConsultation exam={exam} mode={mode} />
          {examTests &&
            examTests.length > 0 &&
            !exam?.endDate &&
            (() => {
              const totalSeconds = examTests.reduce(
                (sum, et) => sum + (et.test?.averageDuration ?? 0),
                0,
              );
              return totalSeconds > 0 ? (
                <Alert variant="info" className="mb-2 py-1 px-3">
                  Szacowany czas trwania badania:{' '}
                  <strong>{Math.round(totalSeconds / 60)} min</strong>
                </Alert>
              ) : null;
            })()}
          <ExamTests
            getData={getData}
            getCount={getCount}
            onRowClick={handleClick}
          />
          {exam?.active && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {(!mode ||
                mode === ArticleMode.Doctor ||
                mode === ArticleMode.Psychologist) && (
                <div className="d-flex align-items-center gap-1">
                  <ExamDeleteLink examId={exam?.id} examNumber={exam?.number} />
                  <Button
                    onClick={() => void nav.examEditor(exam?.id || '')}
                    variant="outline-secondary"
                  >
                    Edycja badania
                  </Button>
                  {!activeBlock && (
                    <Button
                      variant="outline-warning"
                      onClick={() => setShowBlockModal(true)}
                    >
                      Zablokuj
                    </Button>
                  )}
                  {!exam?.online && (
                    <ExamOnline
                      examId={exam?.id}
                      tests={examTests?.map(o => o.test).filter(o => !!o)}
                      online={exam?.online || false}
                      endDate={exam?.endDate}
                      setRefresh={handleOnRefresh}
                      className="ms-1 mb-1"
                    />
                  )}
                </div>
              )}
              <ExamSpecialistRoleEndedLink
                examId={exam?.id}
                examNumber={exam?.number}
                exam={exam}
                mode={mode}
              />
              <ExamEndLink
                onExamChanged={onExamChanged}
                examId={exam?.id || ''}
                endDate={exam?.endDate}
                startDate={exam?.startDate}
                mode={mode}
              />
            </div>
          )}
        </Tab>
        {exam?.id && (
          <Tab eventKey="synevo" title={'SYNEVO'} className="m-3">
            <ExamSynevoTab
              exam={exam}
              refresh={refresh}
              onRefresh={handleOnRefresh}
            />
          </Tab>
        )}
        {exam?.id && (
          <Tab
            eventKey="supportinExaminationHelpExam"
            title={'Badania pomocnicze'}
            className="m-3"
          >
            <Alert variant="info" className="mb-3">
              Aby uwzględnić zmiany w kartach, karty muszą zostać ponownie
              zapisane.
            </Alert>
            <SupportingExaminationView
              examId={exam.id}
              mode={SupportingType.helpExam}
              cardId={id}
            />
          </Tab>
        )}
        {exam?.id && (
          <Tab
            eventKey="supportinExaminationConsultation"
            title={'Konsultacje specjalistyczne'}
            className="m-3"
          >
            <Alert variant="info" className="mb-3">
              Aby uwzględnić zmiany w kartach, karty muszą zostać ponownie
              zapisane.
            </Alert>
            <SupportingExaminationView
              examId={exam.id}
              mode={SupportingType.specialisedConsultation}
              cardId={id}
            />
          </Tab>
        )}
        {exam?.id && auth.check(ALL_WORKERS_REGISTRATION) && (
          <Tab eventKey="payments" title={'Płatności'} className="m-3">
            <div className="d-flex flex-column gap-3">
              <CompanyPrepaymentAlert company={exam.examPackage?.company} />
              <ExamPortalPaymentAlert examId={exam.id} />
              <div className="exam-payment-section">
                <ExamInvoicesList examId={exam.id} />
                <ExamReceiptsList examId={exam.id} refresh={refresh} />
              </div>
              <div className="d-flex gap-3 flex-wrap exam-payment-section">
                <div className="flex-fill">
                  <ExamReceiptEditor exam={exam} onCompleted={editPayment} />
                </div>
                <div className="flex-fill">
                  <ExamPaymentEditor
                    exam={exam as CreateUpdateExamDto}
                    examId={exam.id}
                    onCompleted={editPayment}
                  />
                </div>
              </div>
            </div>
          </Tab>
        )}
      </TabsHistory>
      <ExamBlockModal
        show={showBlockModal}
        onHide={() => setShowBlockModal(false)}
        onConfirm={handleBlock}
      />
      <ExamBlockModal
        show={showEditBlockModal}
        onHide={() => setShowEditBlockModal(false)}
        onConfirm={handleUpdateBlock}
        initialReason={activeBlock?.reason}
        title="Edytuj blokadę"
        confirmLabel="Zapisz"
        blockId={activeBlock?.id}
        getLastEditor={spd.examBlocks.getLastEditor}
      />
    </Card>
  );
};
